import { Check, ChevronRight } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { ConfirmationBadge } from './ConfirmationBadge';
import { PORTAL_ROUTES } from 'utils/constants';
import { canRequestConfirm } from './helper';
import { formatDate } from 'utils/helpers';
import { capitalize } from 'lodash';
import { LocationLink } from 'components/LocationLink';

interface AppointmentTableRowProps {
  row: any;
  index: number;
  confirmingId: string | null;
  onConfirm: (id: string) => void;
}

const BODY_CELL =
  'px-3 py-3 text-sm whitespace-nowrap';

export const AppointmentTableRow = ({ row, index, confirmingId, onConfirm }: AppointmentTableRowProps) => {
  const showConfirm = canRequestConfirm(row);
  const isConfirming = confirmingId === row.id;

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50/50 transition-colors">
      <td className={`${BODY_CELL} text-secondary-400`}>
        <span>{index + 1}</span>
      </td>
      <td className={BODY_CELL}>
        <span>
          {row.provider.name ? (
            <span className="text-sm font-medium text-secondary-800">{row.provider.name}</span>
          ) : (
            <span className="inline-flex text-xs font-medium text-secondary-500 bg-gray-50 border border-dashed border-gray-300 rounded-md px-2.5 py-1">
              Not assigned yet
            </span>
          )}
        </span>
      </td>
      <td className={`${BODY_CELL} text-secondary-600`}>
        <span>{formatDate(row.date, 'short')}</span>
      </td>
      <td className={`${BODY_CELL} text-secondary-600`}>
        <span>{row.startTime} &ndash; {row.endTime}</span>
      </td>
      <td className={BODY_CELL}>
        <span className="inline-flex text-xs font-medium text-secondary-600 bg-gray-50 border border-dashed border-gray-300 rounded-md px-2.5 py-1">
          {capitalize(row.type) || 'Appointment'}
        </span>
      </td>
      <td className={BODY_CELL}>
        <StatusBadge status={row.status} />
      </td>
      <td className={BODY_CELL}>
        <ConfirmationBadge appt={row} />
      </td>
      <td className={BODY_CELL}>
        <div className="flex items-center gap-2">
          {showConfirm && (
            <button
              onClick={() => onConfirm(row.id)}
              disabled={isConfirming}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 border border-green-300 bg-green-50 rounded-md px-3 py-1.5 hover:bg-green-100 transition-colors disabled:opacity-60 disabled:cursor-wait"
            >
              <Check size={13} />
              Confirm
            </button>
          )}
          <LocationLink
            to={`${PORTAL_ROUTES.APPOINTMENTS}/${row.id}`}
            className="inline-flex items-center gap-1.5 text-xs text-secondary-500 border border-gray-200 rounded-md px-3 py-1.5 hover:bg-gray-50 hover:text-secondary-800 transition-colors"
          >
            Details
            <ChevronRight size={13} />
          </LocationLink>
        </div>
      </td>
    </tr>
  );
};
